import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import {
  bulkDeleteEmails,
  bulkMarkEmailsRead,
  bulkMoveEmails,
  starEmail,
  markEmailRead,
} from '@/api/emails';
import { ApiError, isNetworkError } from '@/api/core/client';
import { isOffline } from '@/lib/connectivity';
import { clearRecentlyRemoved } from './emailStateHelpers';

/**
 * Offline action queue.
 *
 * When a mailbox mutation can't reach the server (device offline / server
 * unreachable), the engine keeps its optimistic state and parks a replay
 * descriptor here instead of rolling back. The queue drains FIFO when
 * connectivity returns ('online' event, SSE reconnect, or app boot).
 *
 * Replay safety rests on the semantic idempotency of the operations: marking
 * read twice, moving to a folder it's already in, or deleting an
 * already-deleted message all converge to the same state server-side. There
 * are no client idempotency keys on the bulk endpoints — a replay after a
 * success-with-lost-response re-applies the same converged state.
 */
export type QueuedEmailOp =
  | {
      op: 'bulkMove';
      accountId: string;
      fromFolderId: string;
      destination: string;
      messageIds: string[];
      expandThreads: boolean;
    }
  | {
      op: 'bulkDelete';
      accountId: string;
      folderId: string;
      messageIds: string[];
      expandThreads: boolean;
    }
  | {
      op: 'bulkRead';
      accountId: string;
      folderId: string;
      messageIds: string[];
      read: boolean;
      expandThreads: boolean;
    }
  | { op: 'markRead'; accountId: string; folderId: string; emailId: string; read: boolean }
  | { op: 'star'; accountId: string; folderId: string; emailId: string; starred: boolean };

export interface QueuedItem {
  id: string;
  op: QueuedEmailOp;
  queuedAt: number;
  /** Failed replay attempts while the browser believed it was online. */
  attempts?: number;
}

// A NetworkError while navigator.onLine is true usually means the server is
// down (retry is right), but it can also be a permanently blocked request
// (CSP, extension). Bound the retries so a poisoned item can't pin the queue
// forever. Attempts only increment on online-but-failed tries — genuine
// offline periods never touch the counter.
const MAX_REPLAY_ATTEMPTS = 25;

// Connectivity signals ('online', SSE reconnect, boot) are the primary drain
// triggers, but none of them fire while the SERVER is down and the browser
// still believes it's online — so a stalled drain reschedules itself.
const RETRY_DELAY_MS = 30_000;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Set by emailCommands at module init so successful replays re-stamp the echo
 * shield without a static import cycle (emailCommands already imports us).
 */
let recordPlacementHook:
  | ((emailId: string, accountId: string, destination: string | null) => void)
  | null = null;
export function registerPlacementRecorder(
  hook: (emailId: string, accountId: string, destination: string | null) => void
): void {
  recordPlacementHook = hook;
}

/** Set by RealtimeBridge: reload folders + active mailbox after a drain. */
let postDrainReloadHook: (() => void) | null = null;
export function registerPostDrainReload(hook: () => void): void {
  postDrainReloadHook = hook;
}

async function executeQueuedOp(op: QueuedEmailOp): Promise<void> {
  switch (op.op) {
    case 'bulkMove':
      await bulkMoveEmails(op.accountId, op.messageIds, op.fromFolderId, op.destination, {
        expandThreads: op.expandThreads,
      });
      op.messageIds.forEach((id) => recordPlacementHook?.(id, op.accountId, op.destination));
      return;
    case 'bulkDelete':
      await bulkDeleteEmails(op.accountId, op.messageIds, op.folderId, {
        expandThreads: op.expandThreads,
      });
      return;
    case 'bulkRead':
      await bulkMarkEmailsRead(op.accountId, op.messageIds, op.read, op.folderId, {
        expandThreads: op.expandThreads,
      });
      return;
    case 'markRead':
      await markEmailRead(op.accountId, op.emailId, op.read, op.folderId);
      return;
    case 'star':
      await starEmail(op.accountId, op.emailId, op.starred, op.folderId);
      return;
  }
}

function describeOp(op: QueuedEmailOp): string {
  switch (op.op) {
    case 'bulkMove':
      return `move ${op.messageIds.length} email${op.messageIds.length === 1 ? '' : 's'}`;
    case 'bulkDelete':
      return `delete ${op.messageIds.length} email${op.messageIds.length === 1 ? '' : 's'}`;
    case 'bulkRead':
      return `mark ${op.messageIds.length} email${op.messageIds.length === 1 ? '' : 's'} ${op.read ? 'read' : 'unread'}`;
    case 'markRead':
      return `mark 1 email ${op.read ? 'read' : 'unread'}`;
    case 'star':
      return op.starred ? 'star 1 email' : 'unstar 1 email';
  }
}

interface OfflineQueueState {
  items: QueuedItem[];
  draining: boolean;
  enqueue: (op: QueuedEmailOp) => void;
  drain: () => Promise<void>;
}

export const useOfflineQueueStore = create<OfflineQueueState>()(
  persist(
    (set, get) => ({
      items: [],
      draining: false,

      enqueue: (op) => {
        set((state) => ({
          items: [...state.items, { id: crypto.randomUUID(), op, queuedAt: Date.now() }],
        }));
      },

      drain: async () => {
        if (get().draining || isOffline()) {
          return;
        }
        if (retryTimer) {
          clearTimeout(retryTimer);
          retryTimer = null;
        }
        set({ draining: true });
        let flushed = 0;
        let dropped = 0;
        const dropItem = (item: QueuedItem, error: unknown) => {
          console.error('[offlineQueue] queued op dropped', item.op, error);
          dropped += 1;
          set((state) => ({ items: state.items.filter((entry) => entry.id !== item.id) }));
          // Lift the removal grace so the optimistically-hidden rows can
          // resurface on the post-drain reload instead of staying invisible
          // in a folder the user isn't currently looking at.
          if (item.op.op === 'bulkMove' || item.op.op === 'bulkDelete') {
            item.op.messageIds.forEach((id) => clearRecentlyRemoved(id));
          }
          toast.error(`A queued action failed to sync (${describeOp(item.op)})`);
        };
        try {
          while (true) {
            const item = get().items[0];
            if (!item || isOffline()) {
              break;
            }
            try {
              await executeQueuedOp(item.op);
              flushed += 1;
              set((state) => ({ items: state.items.filter((entry) => entry.id !== item.id) }));
            } catch (error) {
              // Retryable: connectivity loss, auth expiry (the boot drain can
              // fire with a stale token — dropping user actions over a 401
              // would be silent data loss; re-login triggers another drain),
              // and transient server states (5xx while the backend restarts,
              // 408/429). Only a definitive 4xx rejection drops the op.
              const retryable =
                isNetworkError(error) ||
                (error instanceof ApiError &&
                  (error.status === 401 ||
                    error.status === 403 ||
                    error.status === 408 ||
                    error.status === 429 ||
                    error.status >= 500));
              if (retryable) {
                const attempts = (item.attempts ?? 0) + 1;
                if (attempts >= MAX_REPLAY_ATTEMPTS) {
                  dropItem(item, error);
                  continue;
                }
                set((state) => ({
                  items: state.items.map((entry) =>
                    entry.id === item.id ? { ...entry, attempts } : entry
                  ),
                }));
                // Still unreachable — keep the queue, retry on the next
                // connectivity signal.
                break;
              }
              // Real server rejection: the op will never succeed.
              dropItem(item, error);
            }
          }
        } finally {
          set({ draining: false });
          if (flushed > 0 || dropped > 0) {
            postDrainReloadHook?.();
          }
          if (get().items.length && !isOffline() && !retryTimer) {
            retryTimer = setTimeout(() => {
              retryTimer = null;
              void get().drain();
            }, RETRY_DELAY_MS);
          }
        }
      },
    }),
    {
      name: 'offline-queue-storage',
      version: 1,
      partialize: (state) => ({ items: state.items }),
    }
  )
);
